import { Link } from 'react-router-dom';
import Logo from '../ui/Logo';
import { Badge } from '../ui/primitives';
import { Icon } from '../ui/Icon';
import { useBreakpoint } from '../../lib/useBreakpoint';

/**
 * Two-pane layout used by SignIn, Register, and AcceptInvite.
 * Left: form (children). Right: navy testimonial / hero panel with floating docs.
 *
 * On mobile/tablet (<1024px), the right hero pane is hidden — there's no room
 * for both panes without crushing the form. Form pane takes full width.
 *
 * Props:
 *   - children: form content for the left pane
 *   - side.topRight: optional element to render in the top-right corner of the form pane
 */
export default function AuthShell({ children, side }) {
  const { isDesktop, isMobile } = useBreakpoint();
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: isDesktop ? '1fr 1fr' : '1fr',
      minHeight: '100vh',
      background: 'var(--surface)',
      fontFamily: 'var(--font-sans)',
      color: 'var(--text)',
    }}>
      {/* Left: form */}
      <div style={{
        display: 'flex', flexDirection: 'column',
        padding: isMobile ? '24px 20px' : '40px 56px',
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 12, flexWrap: 'wrap',
        }}>
          <Link to="/sign-in" style={{ textDecoration: 'none' }}>
            <Logo size={40} useFile />
          </Link>
          {side?.topRight}
        </div>

        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px 0' }}>
          <div style={{ width: '100%', maxWidth: 380 }}>{children}</div>
        </div>

        <div style={{
          fontSize: 11.5, color: 'var(--text-muted)',
          display: 'flex', gap: 18, flexWrap: 'wrap',
        }}>
          <span>© 2026 MagicIntake</span>
          <a href="#" style={{ color: 'inherit' }}>Privacy</a>
          <a href="#" style={{ color: 'inherit' }}>Terms</a>
          <a href="#" style={{ color: 'inherit' }}>Status</a>
        </div>
      </div>

      {/* Right: testimonial / hero panel — desktop only */}
      {isDesktop && (
      <div style={{
        background: 'linear-gradient(135deg, #0a0f2e 0%, #131a3d 60%, #1e2654 100%)',
        color: '#fff',
        padding: '40px 56px',
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
      }}>
        {/* Decorative radial glow */}
        <div style={{ position: 'absolute', inset: 0, opacity: 0.18, pointerEvents: 'none' }}>
          <svg width="100%" height="100%">
            <defs>
              <radialGradient id="auth-glow" cx="70%" cy="30%">
                <stop offset="0%" stopColor="#3dc75b" stopOpacity="0.6" />
                <stop offset="100%" stopColor="#3dc75b" stopOpacity="0" />
              </radialGradient>
            </defs>
            <rect width="100%" height="100%" fill="url(#auth-glow)" />
          </svg>
        </div>

        <div style={{ position: 'relative' }}>
          <Badge
            variant="accent"
            style={{
              background: 'rgba(61,199,91,0.16)',
              border: '1px solid rgba(61,199,91,0.3)',
              color: '#8be89e',
            }}
          >
            <Icon.sparkle size={10} /> NEW · Auto-classify v3
          </Badge>
        </div>

        {/* Floating doc cards */}
        <div style={{ position: 'relative', flex: 1, marginTop: 40, marginBottom: 40 }}>
          <FloatingDocs />
        </div>

        <div style={{ position: 'relative' }}>
          <div style={{
            fontSize: 22, fontWeight: 600, letterSpacing: '-0.02em',
            lineHeight: 1.25, marginBottom: 18,
          }}>
            &ldquo;MagicIntake replaced four hours of weekly document chasing with about four minutes.&rdquo;
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 40, height: 40, borderRadius: 12, background: '#3dc75b',
              color: '#0a0f2e', display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontWeight: 700,
            }}>
              HD
            </div>
            <div>
              <div style={{ fontSize: 13.5, fontWeight: 600 }}>Haven Del Pino, Esq.</div>
              <div style={{ fontSize: 12, color: '#8be89e' }}>Del Pino &amp; Granados Law Firm</div>
            </div>
          </div>
        </div>
      </div>
      )}
    </div>
  );
}

const FLOATING_DOCS = [
  { top: 10,  left: 10,  label: 'W-9 · Vendor',     badge: 'Approved',    v: 'ok',     rot: -6 },
  { top: 110, left: 230, label: 'Insurance COI',    badge: 'AI · 99%',    v: 'accent', rot: 4  },
  { top: 250, left: 40,  label: 'NDA · Signed',     badge: 'Classifying', v: 'info',   rot: -2 },
  { top: 220, left: 290, label: 'ID Verification',  badge: 'Review',      v: 'warn',   rot: 8  },
];

function FloatingDocs() {
  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      {FLOATING_DOCS.map((d, i) => (
        <div key={i} style={{
          position: 'absolute', top: d.top, left: d.left, width: 280,
          background: '#fff', color: '#0a0f2e', padding: '18px 20px',
          borderRadius: 16, boxShadow: '0 24px 72px rgba(0,0,0,0.45)',
          transform: `rotate(${d.rot}deg)`,
          border: '1px solid rgba(255,255,255,0.2)',
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <div style={{
              width: 42, height: 42, borderRadius: 10, background: '#eef0f7',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#3dc75b', flexShrink: 0,
            }}>
              <Icon.file size={20} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>{d.label}</div>
              <Badge variant={d.v}>{d.badge}</Badge>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
